import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import type { ReactElement } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Title, useDataProvider, useNotify, useTranslate } from "react-admin";
import type { DataProvider } from "react-admin";
import type { VoucherStats } from "@/types";
import { extractErrorMessage } from "@/utilities/errorExtract";
import { homeStyles } from "./home.styles";

type HomeDataProvider = DataProvider & {
  getVoucherStats: () => Promise<{ data: VoucherStats }>;
};

type RewardCardProps = {
  count?: number;
  label: string;
  loading: boolean;
};

const RewardCard = ({ count, label, loading }: RewardCardProps) => (
  <Card sx={homeStyles.statCard}>
    <CardContent sx={{ height: "100%" }}>
      <Chip
        size="small"
        label="Récompense"
        color="secondary"
        sx={{ fontWeight: 800 }}
      />
      <Typography variant="h6" sx={{ mt: 1.5, fontWeight: 800 }}>
        {label}
      </Typography>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="flex-end"
        spacing={0.75}
        sx={{ mt: 3 }}
      >
        {loading ? (
          <Skeleton width={82} height={54} />
        ) : (
          <>
            <Typography variant="h3" color="secondary" fontWeight={900}>
              {count ?? "—"}
            </Typography>
            <Typography variant="body2" color="secondary">
              créées
            </Typography>
          </>
        )}
      </Stack>
    </CardContent>
  </Card>
);

type QuickActionProps = {
  description: string;
  icon: ReactElement;
  label: string;
  to: string;
};

const QuickAction = ({ description, icon, label, to }: QuickActionProps) => (
  <Box component={RouterLink} to={to} sx={homeStyles.actionCard}>
    <Box sx={homeStyles.iconBadge}>{icon}</Box>
    <Box sx={{ ml: 2, minWidth: 0, flex: 1 }}>
      <Typography variant="subtitle1" fontWeight={800}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
    <ArrowForwardRoundedIcon color="secondary" />
  </Box>
);

const AdminHome = () => {
  const [stats, setStats] = useState<VoucherStats | null>(null);
  const [loading, setLoading] = useState(true);
  const dataProvider = useDataProvider() as HomeDataProvider;
  const notify = useNotify();
  const translate = useTranslate();

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dataProvider.getVoucherStats();
      setStats(response.data);
    } catch (error) {
      notify(extractErrorMessage(error), { type: "warning" });
    } finally {
      setLoading(false);
    }
  }, [dataProvider, notify]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return (
    <Box sx={homeStyles.root}>
      <Title title="Accueil" />

      <Box sx={homeStyles.hero}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="caption" sx={homeStyles.eyebrow}>
              GAMEOO Backoffice
            </Typography>
            <Typography variant="h3" sx={homeStyles.title}>
              Bonjour, Administrateur
            </Typography>
            <Typography color="text.secondary" sx={homeStyles.heroText}>
              Retrouvez les chiffres clés des récompenses et accédez rapidement
              aux espaces de gestion.
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              component={RouterLink}
              to="/dashboard"
              variant="contained"
              color="secondary"
              startIcon={<DashboardOutlinedIcon />}
            >
              Dashboard analytique
            </Button>
            <Button
              component={RouterLink}
              to="/vouchers"
              variant="outlined"
              color="secondary"
              startIcon={<CardGiftcardOutlinedIcon />}
            >
              Voir les récompenses
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Typography variant="h5" sx={homeStyles.sectionTitle}>
        Vue d'ensemble
      </Typography>
      <Box sx={homeStyles.statsGrid}>
        <RewardCard
          loading={loading}
          count={stats?.gift_card_count}
          label={translate("ra.gameoo.statsGiftCardLabel")}
        />
        <RewardCard
          loading={loading}
          count={stats?.free_product_count}
          label={translate("ra.gameoo.statsFreeProductLabel")}
        />
        <RewardCard
          loading={loading}
          count={stats?.bogo_count}
          label={translate("ra.gameoo.statsBogoLabel")}
        />
        <RewardCard
          loading={loading}
          count={stats?.discount_count}
          label={translate("ra.gameoo.statsDiscountLabel")}
        />
      </Box>

      <Typography variant="h5" sx={homeStyles.sectionTitle}>
        Gestion rapide
      </Typography>
      <Box sx={homeStyles.actionsGrid}>
        <QuickAction
          to="/entities"
          label="Gérer les entités"
          description="Consulter et administrer les partenaires GAMEOO."
          icon={<BusinessOutlinedIcon />}
        />
        <QuickAction
          to="/vouchers"
          label="Suivre les récompenses"
          description="Retrouver les vouchers et leur état actuel."
          icon={<CardGiftcardOutlinedIcon />}
        />
        <QuickAction
          to="/users"
          label="Gérer les utilisateurs"
          description="Consulter les comptes et leurs informations."
          icon={<PeopleAltOutlinedIcon />}
        />
        <QuickAction
          to="/locations"
          label="Gérer les localisations"
          description="Accéder aux points de vente et à leur rattachement."
          icon={<LocationOnOutlinedIcon />}
        />
      </Box>
    </Box>
  );
};

export default AdminHome;
